import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import vehicleImage from "app/assets/vechile.svg";
import React from "react";
import { useTranslation } from "react-i18next";

import { QuoteDetailsSummary } from "../../../types";
import { useStyles } from "../styles";

type FieldProps = {
  label: string;
  value: string;
  hasDropdown?: boolean;
};

type Props = {
  data: QuoteDetailsSummary;
};

const DetailField = ({ label, value, hasDropdown = false }: FieldProps) => {
  const styles = useStyles();

  return (
    <Box className={styles.field}>
      <Typography className={styles.label}>{label}</Typography>
      <Box className={styles.valueRow}>
        {hasDropdown ? (
          <KeyboardArrowDownIcon className={styles.chevron} />
        ) : null}
        <Typography className={styles.value}>{value}</Typography>
      </Box>
    </Box>
  );
};

export const QuoteSummaryMetrics = ({ data }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const money = (amount: string | number) => (
    <span dir="ltr">{t("QUOTES.CURRENCY", { amount })}</span>
  );

  return (
    <Box className={styles.metricsCard}>
      <Box className={styles.metricsGrid}>
        <Box className={styles.metric}>
          <Typography className={styles.metricLabel}>
            {t("QUOTES.DETAILS.METRICS.TOTAL_SALE")}
          </Typography>
          <Typography className={styles.metricValue}>
            {money(data.totalSale)}
          </Typography>
          <Typography className={styles.profitText}>
            {t("QUOTES.DETAILS.METRICS.PROFIT_LABEL")}
            {": "}
            {money(data.profitAmount)}
            {` ${data.profitPercent}%`}
          </Typography>
        </Box>

        <Box className={styles.metric}>
          <Typography className={styles.metricLabel}>
            {t("QUOTES.DETAILS.METRICS.THEORETICAL_PRICE")}
          </Typography>
          <Typography className={styles.metricValue}>
            {money(data.theoreticalPrice)}
          </Typography>
        </Box>

        <Box className={styles.metric}>
          <Typography className={styles.metricLabel}>
            {t("QUOTES.DETAILS.METRICS.TOTAL_COST")}
          </Typography>
          <Typography className={styles.metricValue}>
            {money(data.totalCost)}
          </Typography>
        </Box>

        <Box className={styles.metric}>
          <Typography className={styles.metricLabel}>
            {t("QUOTES.DETAILS.METRICS.TOTAL_MATERIAL_COST")}
          </Typography>
          <Typography className={styles.metricValue}>
            {money(data.totalMaterialCost)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SummaryBar = ({ data }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <>
      <Box className={styles.summaryBar}>
        <Typography className={styles.summaryTitle}>
          {t("QUOTES.DETAILS.HEADER.TITLE")}
        </Typography>
        <Box className={styles.fieldsRow}>
          <Box className={styles.imageCell}>
            <img
              src={vehicleImage}
              alt={t("QUOTES.DETAILS.HEADER.VEHICLE_IMAGE_ALT")}
              className={styles.vehicleImage}
            />
          </Box>

          <DetailField
            label={t("QUOTES.DETAILS.HEADER.WORK_ORDER_NO")}
            value={data.workOrderNo}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.CUSTOMER_NAME")}
            value={t(data.customerNameKey)}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.SUB_CUSTOMER")}
            value={t(data.subCustomerKey)}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.CHASSIS")}
            value={data.chassis}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.MANUFACTURER")}
            value={t(data.manufacturerKey)}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.MODEL")}
            value={data.model}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.AXLE_STRUCTURE")}
            value={data.axleConfiguration}
            hasDropdown
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.YEAR")}
            value={data.year}
          />
          <DetailField
            label={t("QUOTES.DETAILS.HEADER.BOX_TYPE")}
            value={t(data.boxTypeKey)}
            hasDropdown
          />
        </Box>
      </Box>
      <QuoteSummaryMetrics data={data} />
    </>
  );
};

export default SummaryBar;
